import { useMemo, useState } from 'react';
import {
  Activity, AlertCircle, Beef, BookOpen, Bot, Camera, ChevronDown, ChevronLeft, ChevronRight,
  ClipboardList, Droplets, Flame, Plus, ScanLine, Search, Send, Sparkles, Star, Trash2,
  TrendingUp, User, Wheat, X, Zap,
} from 'lucide-react';
import {
  Area, AreaChart, CartesianGrid, Legend, Line, LineChart, ResponsiveContainer, Tooltip, XAxis, YAxis,
} from 'recharts';
import { DEMO_BARCODE, DEMO_LOCAL_MEALS, DEMO_NICO_REPLIES, DEMO_PHOTO_PLATE, FOOD_QUICK_ADD } from './demoFixtures';

const MEAL_TABS = [
  { id: 'Desayuno', label: '🌅 Desayuno' },
  { id: 'Almuerzo', label: '☀️ Almuerzo' },
  { id: 'Cena', label: '🌙 Cena' },
  { id: 'Snack', label: '🍎 Snack' },
  { id: 'Otra', label: '⭐ Otra comida' },
];

const CATALOG = [
  ...FOOD_QUICK_ADD,
  { name: 'Arepa', meal: 'Desayuno', kcal: 180, protein: 4, carbs: 32, fat: 4, qty: '50g' },
  { name: 'Huevo entero', meal: 'Desayuno', kcal: 78, protein: 6, carbs: 1, fat: 5, qty: '1 unid.' },
  { name: 'Atún en agua', meal: 'Almuerzo', kcal: 116, protein: 26, carbs: 0, fat: 1, qty: '100g' },
];

const RECIPES = [
  { name: 'Batido de Fresa', category: 'Snack', minutes: 5, servings: 1, kcal: 180, desc: 'Batido refrescante y ligero.', objective: 'Mantener', tone: 'from-pink-100 to-rose-50', emoji: '🥤' },
  { name: 'Bagel con Salmón', category: 'Desayuno', minutes: 12, servings: 1, kcal: 360, desc: 'Desayuno elegante y balanceado.', objective: 'Mantener', tone: 'from-orange-100 to-amber-50', emoji: '🥯' },
  { name: 'Bowl de quinoa', category: 'Almuerzo', minutes: 20, servings: 1, kcal: 410, desc: 'Almuerzo con proteína y fibra.', objective: 'Bajar peso', tone: 'from-lime-100 to-green-50', emoji: '🥗' },
  { name: 'Tacos de pollo', category: 'Cena', minutes: 25, servings: 2, kcal: 480, desc: 'Cena alta en proteína.', objective: 'Ganar músculo', tone: 'from-yellow-100 to-orange-50', emoji: '🌮' },
];

const NICO_CATS = [
  { label: '🍗 Alimentos', questions: ['¿Cómo voy con la proteína?', '¿Qué ceno si ya comí pollo?', '¿Puedo comer fruta de noche?'] },
  { label: '⚖️ Peso y metas', questions: ['¿Cómo voy con la proteína?'] },
];

const WEIGHT_SERIES = [
  { date: '16 may', weight: 79 },
  { date: '30 may', weight: 77.4 },
  { date: '13 jun', weight: 77.1 },
  { date: '27 jun', weight: 75.6 },
  { date: '11 jul', weight: 75.4 },
  { date: '25 jul', weight: 74.2 },
  { date: '8 ago', weight: 73.6 },
  { date: '22 ago', weight: 73.6 },
  { date: '5 sep', weight: 73.2 },
  { date: '19 sep', weight: 73.8 },
  { date: '3 oct', weight: 73.7 },
  { date: '21 oct', weight: 74.9 },
];

const MEASURE_SERIES = WEIGHT_SERIES.map((row, i) => ({
  date: row.date,
  Pecho: 108 - i * 0.6,
  Hombros: 112 - i * 0.4,
  'Bícep D': 34 - i * 0.15,
  'Abd.↑': 92 - i * 0.5,
  'Abd.⊙': 90 - i * 0.45,
  'Abd.↓': 88 - i * 0.4,
  'Muslo D': 62 - i * 0.2,
}));

const PILL = {
  rendimiento: [
    { id: 'muy_malo', label: 'Muy malo', color: '#ef4444' },
    { id: 'malo', label: 'Malo', color: '#f97316' },
    { id: 'normal', label: 'Normal', color: '#eab308' },
    { id: 'bueno', label: 'Bueno', color: '#84cc16' },
    { id: 'muy_bueno', label: 'Muy bueno', color: '#22c55e' },
  ],
  motivacion: [
    { id: 'baja', label: 'Baja', color: '#ef4444' },
    { id: 'normal', label: 'Normal', color: '#eab308' },
    { id: 'alta', label: 'Alta', color: '#22c55e' },
  ],
  hambre: [
    { id: 'ausente', label: 'Ausente', color: '#22c55e' },
    { id: 'soportable', label: 'Soportable', color: '#84cc16' },
    { id: 'fuerte', label: 'Fuerte', color: '#f97316' },
    { id: 'insoportable', label: 'Insoportable', color: '#ef4444' },
  ],
  cansancio: [
    { id: 'nada', label: 'Nada', color: '#22c55e' },
    { id: 'poco', label: 'Poco', color: '#84cc16' },
    { id: 'mucho', label: 'Mucho', color: '#f97316' },
    { id: 'muchisimo', label: 'Muchísimo', color: '#ef4444' },
  ],
  estres: [
    { id: 'ausente', label: 'Ausente', color: '#22c55e' },
    { id: 'bajo', label: 'Bajo', color: '#84cc16' },
    { id: 'alto', label: 'Alto', color: '#f97316' },
    { id: 'muy_alto', label: 'Muy alto', color: '#ef4444' },
  ],
  sueno: [
    { id: 'muy_baja', label: 'Muy baja', color: '#ef4444' },
    { id: 'baja', label: 'Baja', color: '#f97316' },
    { id: 'alta', label: 'Alta', color: '#84cc16' },
    { id: 'muy_alta', label: 'Muy alta', color: '#22c55e' },
  ],
};

function MacroTile({ icon: Icon, color, value, unit, label }) {
  return (
    <div className="card text-center">
      <div className={`w-12 h-12 rounded-xl ${color} flex items-center justify-center mx-auto mb-3`}>
        <Icon size={22} className="text-white" />
      </div>
      <p className="text-3xl font-bold text-gray-800">{value}</p>
      <p className="text-sm text-gray-500">{unit}</p>
      <p className="text-xs font-medium text-gray-600 mt-1">{label}</p>
    </div>
  );
}

export function DemoPlanView({ food }) {
  return (
    <div className="space-y-4 max-w-4xl">
      <div className="flex items-start justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-gray-800">Mi Plan Nutricional</h1>
          <p className="text-gray-500 text-sm mt-1">Plan calculado según tus datos personales</p>
        </div>
        <button type="button" className="btn-secondary text-sm">Actualizar datos</button>
      </div>
      <div className="flex items-center gap-2 p-3 bg-blue-50 border border-blue-200 rounded-xl text-sm text-blue-700">
        <AlertCircle size={16} /> Tu entrenador ha personalizado este plan nutricional para ti.
      </div>
      <div className="grid grid-cols-2 gap-3">
        <MacroTile icon={Zap} color="bg-orange-500" value={food.goal_kcal} unit="kcal" label="Calorías diarias" />
        <MacroTile icon={Beef} color="bg-red-500" value={food.goal_protein} unit="g/día" label="Proteínas" />
        <MacroTile icon={Wheat} color="bg-yellow-500" value={food.goal_carbs} unit="g/día" label="Carbohidratos" />
        <MacroTile icon={Droplets} color="bg-blue-500" value={food.goal_fat} unit="g/día" label="Grasas" />
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div className="card bg-blue-50 border border-blue-100 text-center">
          <Droplets className="mx-auto text-blue-500" size={28} />
          <p className="text-3xl font-bold text-blue-600 mt-1">8</p>
          <p className="text-xs text-blue-500 font-medium">vasos de agua/día</p>
          <p className="text-xs text-gray-400 mt-1">Asignado por tu entrenador</p>
        </div>
        <div className="card bg-green-50 border border-green-100 text-center">
          <p className="text-2xl">🚶</p>
          <p className="text-3xl font-bold text-green-600">8.000</p>
          <p className="text-xs text-green-600 font-medium">pasos diarios</p>
          <p className="text-xs text-gray-400 mt-1">Asignado por tu entrenador</p>
        </div>
      </div>
      <div className="card">
        <h2 className="font-bold text-gray-800 mb-3">Tus datos</h2>
        <div className="grid grid-cols-2 gap-3 text-sm">
          <div><p className="text-gray-400">Peso</p><p className="font-medium">{food.plan.peso} kg</p></div>
          <div><p className="text-gray-400">Altura</p><p className="font-medium">{food.plan.altura} cm</p></div>
          <div><p className="text-gray-400">Objetivo</p><p className="font-medium">📉 Bajar de peso</p></div>
          <div><p className="text-gray-400">Actividad</p><p className="font-medium">Moderado</p></div>
        </div>
      </div>
    </div>
  );
}

export function DemoNutritionView({ food, addMeal }) {
  const [meal, setMeal] = useState('Desayuno');
  const [query, setQuery] = useState('');
  const [scanHit, setScanHit] = useState(null);
  const [photoOpen, setPhotoOpen] = useState(false);
  const [usualOpen, setUsualOpen] = useState(true);
  const [filesOpen, setFilesOpen] = useState(false);
  const rows = food.meals.filter((m) => (meal === 'Otra' ? m.meal === 'Otra' : m.meal === meal));
  const found = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return [];
    return CATALOG.filter((item) => item.name.toLowerCase().includes(q)).slice(0, 6);
  }, [query]);
  const totals = rows.reduce((acc, row) => ({
    kcal: acc.kcal + row.kcal,
    protein: acc.protein + row.protein,
    carbs: acc.carbs + (row.carbs || 0),
    fat: acc.fat + (row.fat || 0),
  }), { kcal: 0, protein: 0, carbs: 0, fat: 0 });

  return (
    <div className="space-y-4 max-w-5xl">
      <h1 className="text-2xl font-bold text-gray-800">Registro Nutricional</h1>
      <div id="demo-local" className="space-y-2">
        <p className="text-sm font-semibold text-gray-700">Comidas locales</p>
        <div className="flex gap-2 overflow-x-auto pb-1">
          {DEMO_LOCAL_MEALS.map((item) => (
            <button key={item.id} type="button" className="shrink-0 text-left bg-white border border-gray-200 rounded-xl px-3 py-2 hover:border-[#64ba30]" onClick={() => addMeal({ ...item, meal })}>
              <p className="text-[10px] font-semibold text-[#64ba30]">{item.restaurant}</p>
              <p className="text-sm font-medium text-gray-800">{item.name}</p>
            </button>
          ))}
        </div>
      </div>
      <div className="flex gap-1.5 overflow-x-auto pb-1">
        {MEAL_TABS.map((tab) => (
          <button key={tab.id} type="button" onClick={() => setMeal(tab.id)} className={`px-3 py-2 rounded-xl text-xs font-medium whitespace-nowrap ${meal === tab.id ? 'bg-[#3f8f22] text-white' : 'bg-white text-gray-600 border border-gray-200'}`}>
            {tab.label} ({food.meals.filter((m) => m.meal === tab.id).length})
          </button>
        ))}
      </div>
      <div className="grid lg:grid-cols-2 gap-4">
        <div className="card space-y-3" id="demo-manual">
          <div className="flex items-center justify-between">
            <h2 className="font-bold text-gray-800">Agregar a {meal}</h2>
            <span className="text-xs text-gray-500 flex items-center gap-1"><Sparkles size={12} /> Sugerencias</span>
          </div>
          <div className="relative">
            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <input className="input pl-9" placeholder="Buscar alimento..." value={query} onChange={(e) => setQuery(e.target.value)} />
          </div>
          <button id="demo-scan" type="button" className="w-full flex items-center justify-center gap-2 py-2.5 border-2 border-green-400 text-green-600 rounded-xl text-sm font-medium" onClick={() => setScanHit(DEMO_BARCODE)}>
            <ScanLine size={16} /> Escanear código de barras
          </button>
          {scanHit ? (
            <div className="bg-green-50 border border-green-200 rounded-xl p-3 flex items-center justify-between gap-2">
              <div>
                <p className="text-sm font-semibold">{scanHit.name}</p>
                <p className="text-xs text-gray-500">{scanHit.kcal} kcal · código {scanHit.code}</p>
              </div>
              <button type="button" className="btn-primary text-xs" onClick={() => { addMeal({ ...scanHit, meal }); setScanHit(null); }}>Registrar</button>
            </div>
          ) : null}
          {found.map((item) => (
            <button key={item.name} type="button" className="w-full text-left px-3 py-2 rounded-lg hover:bg-gray-50 border border-gray-100" onClick={() => { addMeal({ ...item, meal }); setQuery(''); }}>
              <p className="text-sm font-medium">{item.name}</p>
              <p className="text-xs text-gray-400">{item.kcal} kcal</p>
            </button>
          ))}
        </div>
        <div className="card">
          <h2 className="font-bold text-gray-800 mb-3">{meal}</h2>
          {rows.length === 0 ? <p className="text-sm text-gray-400 py-8 text-center">Sin registros en esta comida</p> : rows.map((row) => (
            <div key={row.id} className="flex justify-between gap-2 py-2 border-b border-gray-50 text-sm">
              <div>
                <p className="font-medium text-gray-800">{row.name}</p>
                <p className="text-xs text-gray-500">{row.kcal} kcal · P {row.protein}g · C {row.carbs || 0}g · G {row.fat || 0}g</p>
              </div>
              <Trash2 size={14} className="text-gray-300" />
            </div>
          ))}
          {rows.length > 0 ? (
            <p className="text-xs text-gray-500 pt-2 flex justify-between">
              <span>{rows.length} alimento(s)</span>
              <span>{totals.kcal} kcal · P {totals.protein}g · C {totals.carbs}g · G {totals.fat}g</span>
            </p>
          ) : null}
        </div>
      </div>
      <div className="bg-white border border-gray-200 rounded-2xl overflow-hidden">
        <button type="button" className="w-full flex items-center justify-between px-4 py-3 text-sm font-medium text-gray-700" onClick={() => setUsualOpen((v) => !v)}>
          <span className="flex items-center gap-2"><BookOpen size={16} className="text-indigo-500" /> Comidas habituales <span className="bg-indigo-100 text-indigo-700 text-xs px-2 py-0.5 rounded-full">{DEMO_LOCAL_MEALS.length}</span></span>
          <ChevronDown size={16} className={usualOpen ? 'rotate-180' : ''} />
        </button>
        {usualOpen ? (
          <div className="px-4 pb-3 space-y-2">
            {DEMO_LOCAL_MEALS.map((item) => (
              <button key={item.id} type="button" className="w-full text-left text-sm border border-gray-100 rounded-xl px-3 py-2" onClick={() => addMeal({ ...item, meal })}>
                <span className="font-medium">{item.name}</span>
                <span className="text-xs text-gray-400 ml-2">{item.restaurant} · {item.kcal} kcal</span>
              </button>
            ))}
          </div>
        ) : null}
      </div>
      <div className="bg-white border border-gray-200 rounded-2xl">
        <button type="button" className="w-full flex items-center justify-between px-4 py-3 text-sm text-gray-700" onClick={() => setFilesOpen((v) => !v)}>
          <span>Archivos de mi entrenador</span><ChevronDown size={16} />
        </button>
        {filesOpen ? <p className="px-4 pb-3 text-sm text-gray-500">Plan_semana.pdf · Foto_progreso.jpg</p> : null}
      </div>
      <div id="demo-photo" className="bg-white border border-gray-200 rounded-2xl">
        <button type="button" className="w-full flex items-center gap-2 px-4 py-3 text-sm text-gray-700" onClick={() => setPhotoOpen((v) => !v)}>
          <Camera size={16} /> Registro fotográfico
        </button>
        {photoOpen ? (
          <div className="px-4 pb-4 space-y-2">
            <div className="h-24 rounded-xl bg-[#f4f7ee] flex items-center justify-center text-sm text-gray-600">{DEMO_PHOTO_PLATE.name} · {DEMO_PHOTO_PLATE.grams} g</div>
            <button type="button" className="btn-primary text-sm" onClick={() => addMeal({ ...DEMO_PHOTO_PLATE, meal })}>Confirmar gramos</button>
          </div>
        ) : null}
      </div>
    </div>
  );
}

export function DemoRecipesView({ addMeal, setView }) {
  const [category, setCategory] = useState('Todas');
  const [q, setQ] = useState('');
  const list = RECIPES.filter((r) => (category === 'Todas' || r.category === category) && r.name.toLowerCase().includes(q.toLowerCase()));
  return (
    <div className="max-w-4xl">
      <div className="flex items-center gap-3 mb-4">
        <span className="text-4xl">👨‍🍳</span>
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Recetas del Chef Nico</h1>
          <p className="text-sm text-gray-500">Recetas saludables para alcanzar tus metas</p>
        </div>
      </div>
      <div className="flex gap-2 mb-3">
        <div className="relative flex-1">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
          <input className="w-full pl-9 pr-4 py-2.5 border border-gray-200 rounded-xl text-sm" placeholder="Buscar receta..." value={q} onChange={(e) => setQ(e.target.value)} />
        </div>
        <button type="button" className="px-4 py-2.5 bg-green-500 text-white rounded-xl text-sm font-medium">Buscar</button>
      </div>
      <div className="flex gap-2 overflow-x-auto mb-3">
        {['Todas', 'Desayuno', 'Almuerzo', 'Cena', 'Snack', 'Postre'].map((cat) => (
          <button key={cat} type="button" onClick={() => setCategory(cat)} className={`px-3 py-1.5 rounded-full text-xs font-medium border ${category === cat ? 'bg-green-500 text-white border-green-500' : 'bg-white text-gray-600 border-gray-200'}`}>{cat}</button>
        ))}
      </div>
      <select className="w-full mb-4 px-3 py-2 border border-gray-200 rounded-xl text-sm"><option>Todos los objetivos</option></select>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        {list.map((r) => (
          <div key={r.name} className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
            <div className={`h-40 bg-gradient-to-br ${r.tone} flex items-center justify-center text-5xl`}>{r.emoji}</div>
            <div className="p-4">
              <div className="flex justify-between gap-2">
                <h3 className="font-bold text-gray-800 text-sm">{r.name}</h3>
                <span className="text-xs px-2 py-0.5 rounded-full bg-orange-100 text-orange-700">{r.category}</span>
              </div>
              <p className="text-xs text-gray-500 my-2">{r.desc}</p>
              <p className="text-xs text-gray-400">{r.minutes} min · {r.servings} porc. · {r.kcal} kcal</p>
              <p className="text-xs text-green-600 font-medium mt-2">{r.objective}</p>
              <button type="button" className="btn-primary text-xs mt-3" onClick={() => { addMeal({ name: r.name, meal: r.category === 'Todas' ? 'Almuerzo' : r.category, kcal: r.kcal, protein: 20, carbs: 30, fat: 8 }); setView('nutrition'); }}>Preparé esta</button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

export function DemoChatView({ visitorName }) {
  const [cat, setCat] = useState(0);
  const [text, setText] = useState('');
  const [log, setLog] = useState([
    { role: 'assistant', text: `Hola ${visitorName}. Puedo ayudarte con nutrición, snacks, reemplazos y los macros de tu plan.` },
  ]);
  const ask = (question) => {
    const answer = DEMO_NICO_REPLIES[question] || 'En el demo Nico responde las preguntas de ejemplo. En Food Plan real conversa con lo que registraste hoy.';
    setLog((prev) => [...prev, { role: 'user', text: question }, { role: 'assistant', text: answer }]);
    setText('');
  };
  return (
    <div className="max-w-3xl flex flex-col min-h-[70vh]">
      <div className="flex items-center gap-3 mb-4">
        <div className="w-10 h-10 rounded-full bg-[#3f8f22] flex items-center justify-center"><Bot size={18} className="text-white" /></div>
        <div>
          <h1 className="text-xl font-bold text-gray-800">Chat Nico</h1>
          <p className="text-sm text-gray-500">Nutrición, snacks, reemplazos y macros de tu plan</p>
        </div>
      </div>
      <div className="flex-1 space-y-4 mb-3">
        {log.map((m, i) => (
          <div key={i} className={`flex gap-3 ${m.role === 'user' ? 'flex-row-reverse' : ''}`}>
            <div className={`w-8 h-8 rounded-full flex items-center justify-center ${m.role === 'assistant' ? 'bg-[#3f8f22]' : 'bg-gray-200'}`}>
              {m.role === 'assistant' ? <Bot size={16} className="text-white" /> : <User size={16} className="text-gray-600" />}
            </div>
            <div className={`max-w-[80%] rounded-2xl px-4 py-3 text-sm ${m.role === 'assistant' ? 'bg-white border border-gray-100 text-gray-800' : 'bg-[#3f8f22] text-white'}`}>{m.text}</div>
          </div>
        ))}
      </div>
      <div className="mb-2 border border-gray-200 rounded-2xl bg-white overflow-hidden">
        <p className="px-4 py-2.5 text-sm font-medium text-gray-600">💡 Preguntas sugeridas</p>
        <div className="flex gap-1 px-3">
          {NICO_CATS.map((c, i) => (
            <button key={c.label} type="button" onClick={() => setCat(i)} className={`text-xs px-3 py-1.5 rounded-full ${cat === i ? 'bg-[#3f8f22] text-white' : 'bg-gray-100 text-gray-600'}`}>{c.label}</button>
          ))}
        </div>
        <div className="flex gap-2 overflow-x-auto px-3 py-2.5">
          {NICO_CATS[cat].questions.map((q) => (
            <button key={q} type="button" className="text-xs bg-green-50 text-green-800 border border-green-200 rounded-full px-3 py-1.5 whitespace-nowrap" onClick={() => ask(q)}>{q}</button>
          ))}
        </div>
      </div>
      <form className="flex gap-2" onSubmit={(e) => { e.preventDefault(); if (text.trim()) ask(text.trim()); }}>
        <input className="input flex-1" placeholder="Escribe tu consulta nutricional..." value={text} onChange={(e) => setText(e.target.value)} />
        <button type="submit" className="btn-primary px-4"><Send size={18} /></button>
      </form>
    </div>
  );
}

export function DemoMeasuresView() {
  const [video, setVideo] = useState(false);
  const [unit, setUnit] = useState('kg');
  const weight = WEIGHT_SERIES.map((row) => ({ ...row, weight: unit === 'kg' ? row.weight : Math.round(row.weight * 2.205 * 10) / 10 }));
  const first = weight[0].weight;
  const last = weight[weight.length - 1].weight;
  const delta = Math.round((last - first) * 10) / 10;
  return (
    <div className="space-y-4 max-w-4xl">
      <div className="flex items-start justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-gray-800">Medidas Corporales</h1>
          <p className="text-sm text-gray-500">{MEASURE_SERIES.length} registro(s)</p>
          <button type="button" className="inline-flex items-center gap-1.5 text-xs text-red-500 font-medium mt-1" onClick={() => setVideo(true)}>
            ▶ ¿Cómo tomar las medidas correctamente?
          </button>
        </div>
        <button type="button" className="btn-primary text-sm flex items-center gap-2"><Plus size={16} /> Nueva medición</button>
      </div>
      <div className="card">
        <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
          <h2 className="font-bold text-gray-800">📉 Evolución del Peso</h2>
          <button type="button" className="text-xs px-3 py-1 border-2 border-green-300 text-green-700 rounded-full" onClick={() => setUnit((u) => (u === 'kg' ? 'lbs' : 'kg'))}>Ver en {unit === 'kg' ? 'lbs' : 'kg'}</button>
          <p className="text-sm text-gray-600">{first} {unit} → <span className="font-bold text-green-600">{last} {unit}</span> <span className="text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded-full">{delta} {unit}</span></p>
        </div>
        <ResponsiveContainer width="100%" height={220}>
          <AreaChart data={weight}>
            <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
            <XAxis dataKey="date" tick={{ fontSize: 11 }} />
            <YAxis tick={{ fontSize: 11 }} domain={['dataMin - 1', 'dataMax + 1']} />
            <Tooltip />
            <Area type="monotone" dataKey="weight" name="Peso" stroke="#13c918" strokeWidth={2.5} fill="#13c91833" />
          </AreaChart>
        </ResponsiveContainer>
      </div>
      <div className="card">
        <h2 className="font-bold text-gray-800 mb-4">📏 Evolución de Medidas (cm)</h2>
        <ResponsiveContainer width="100%" height={260}>
          <LineChart data={MEASURE_SERIES}>
            <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
            <XAxis dataKey="date" tick={{ fontSize: 11 }} />
            <YAxis tick={{ fontSize: 11 }} />
            <Tooltip />
            <Legend />
            <Line type="monotone" dataKey="Pecho" stroke="#13c918" dot={false} />
            <Line type="monotone" dataKey="Hombros" stroke="#3b82f6" dot={false} />
            <Line type="monotone" dataKey="Bícep D" stroke="#f59e0b" dot={false} />
            <Line type="monotone" dataKey="Abd.↑" stroke="#ef4444" dot={false} />
            <Line type="monotone" dataKey="Muslo D" stroke="#8b5cf6" dot={false} />
          </LineChart>
        </ResponsiveContainer>
      </div>
      {video ? (
        <div className="fixed inset-0 z-[80] bg-black/60 flex items-center justify-center p-4" onClick={() => setVideo(false)}>
          <div className="bg-white rounded-2xl w-full max-w-2xl overflow-hidden" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between px-4 py-3">
              <p className="font-bold text-gray-800">¿Cómo tomar las medidas correctamente?</p>
              <button type="button" onClick={() => setVideo(false)}><X size={18} /></button>
            </div>
            <iframe title="Cómo tomar las medidas" className="w-full aspect-video" src="https://www.youtube.com/embed/qVg9XKWLnH8" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" allowFullScreen />
          </div>
        </div>
      ) : null}
    </div>
  );
}

function PillGroup({ label, icon: Icon, options, value, onChange }) {
  return (
    <div className="space-y-2">
      <div className="flex items-center gap-2 text-sm font-semibold text-gray-700"><Icon size={15} /> {label}</div>
      <div className="flex gap-2 flex-wrap">
        {options.map((opt) => (
          <button key={opt.id} type="button" onClick={() => onChange(opt.id)} className="px-3 py-1.5 rounded-full text-xs font-medium border-2" style={value === opt.id ? { background: opt.color, borderColor: opt.color, color: '#fff' } : { background: '#fff', borderColor: '#e5e7eb', color: '#4b5563' }}>
            {opt.label}
          </button>
        ))}
      </div>
    </div>
  );
}

export function DemoReportView() {
  const [form, setForm] = useState({
    rendimiento: 'bueno', motivacion: 'alta', hambre: 'soportable',
    cansancio: 'poco', estres: 'bajo', sueno: 'alta', saved: false,
  });
  const set = (key, value) => setForm((prev) => ({ ...prev, [key]: value, saved: false }));
  return (
    <div className="max-w-2xl space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2"><ClipboardList className="text-green-500" size={26} /> Reporte Diario</h1>
          <p className="text-sm text-gray-500">Registra cómo te sientes hoy (opcional)</p>
        </div>
        {form.saved ? <span className="text-xs text-green-600 bg-green-50 px-3 py-1 rounded-full border border-green-200">Guardado</span> : null}
      </div>
      <div className="bg-white rounded-2xl border border-gray-100 p-4 flex items-center justify-between">
        <ChevronLeft size={20} className="text-gray-400" />
        <div className="text-center">
          <p className="font-bold text-gray-900 capitalize">jueves 22 de octubre</p>
          <span className="text-xs text-green-600 font-medium">Hoy</span>
        </div>
        <ChevronRight size={20} className="text-gray-300" />
      </div>
      <div className="bg-white rounded-2xl border border-gray-100 p-5 space-y-3">
        <h2 className="font-bold text-gray-800 flex items-center gap-2"><Activity size={17} className="text-blue-500" /> Peso y datos básicos</h2>
        <label className="text-xs text-gray-500">Peso (kg)
          <input className="input mt-1 w-32" defaultValue="72.5" />
        </label>
      </div>
      <div className="bg-white rounded-2xl border border-gray-100 p-5 space-y-5">
        <h2 className="font-bold text-gray-800 flex items-center gap-2"><Zap size={17} className="text-yellow-500" /> Factores de rendimiento</h2>
        <PillGroup label="Rendimiento" icon={TrendingUp} options={PILL.rendimiento} value={form.rendimiento} onChange={(v) => set('rendimiento', v)} />
        <PillGroup label="Motivación" icon={Zap} options={PILL.motivacion} value={form.motivacion} onChange={(v) => set('motivacion', v)} />
        <PillGroup label="Hambre" icon={Flame} options={PILL.hambre} value={form.hambre} onChange={(v) => set('hambre', v)} />
        <PillGroup label="Cansancio" icon={Activity} options={PILL.cansancio} value={form.cansancio} onChange={(v) => set('cansancio', v)} />
        <PillGroup label="Estrés" icon={AlertCircle} options={PILL.estres} value={form.estres} onChange={(v) => set('estres', v)} />
      </div>
      <div className="bg-white rounded-2xl border border-gray-100 p-5 space-y-3">
        <h2 className="font-bold text-gray-800 flex items-center gap-2"><Star size={17} className="text-indigo-500" /> Sueño</h2>
        <label className="text-sm text-gray-700">Horas de sueño
          <input className="input mt-1 w-28" defaultValue="7.5" />
        </label>
        <PillGroup label="Calidad del sueño" icon={Star} options={PILL.sueno} value={form.sueno} onChange={(v) => set('sueno', v)} />
      </div>
      <button type="button" className="btn-primary" onClick={() => setForm((prev) => ({ ...prev, saved: true }))}>Guardar reporte</button>
      <p className="text-xs text-gray-400">Demo · se queda en este navegador.</p>
    </div>
  );
}
